import {
  AppDataComponent,
  AppUnifiedComponentData,
  DocumentServicesObject,
  EditorClientSpecMapEntry,
  UnifiedWidget,
} from '@wix/document-services-types';
import { Item, Section } from '@wix/editor-platform-host-integration-apis';
import {
  ComponentType,
  Preset,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import { PlatformContext } from '../../../types/platformApi';
import { ALLOWED_EXTENSIONS } from './constants';
import {
  AppDataComponentWithItems,
  PlatformAppCategoryDefinition,
  PlatformAppItemDefinition,
  PlatformAppWithCategoryDefinition,
} from './types';
import { AppData } from '../../../types/unifiedComponents';
import { mapDefinitionToItem, mapDefinitionToSection } from './mappers';

export const isValidUnifiedPreset = (
  preset?: Partial<Preset>,
): preset is Preset =>
  Boolean(preset && preset.id && preset.name && preset.thumbnailUrl);

export const isValidBlocksPresetSize = (
  size?: Partial<PlatformAppItemDefinition['size']>,
): size is PlatformAppItemDefinition['size'] =>
  Boolean(size && size.width && size.height);

export const isValidBlocksPreset = (
  preset?: PlatformAppCategoryDefinition['items'][number],
): preset is PlatformAppItemDefinition =>
  Boolean(
    preset &&
      preset.widgetId &&
      preset.presetId &&
      preset.desc &&
      preset.image &&
      isValidBlocksPresetSize(preset.size),
  );

export const isValidPreset = (
  preset?: Partial<Preset> | PlatformAppCategoryDefinition['items'][number],
): preset is Preset | PlatformAppItemDefinition =>
  isValidUnifiedPreset(preset) || isValidBlocksPreset(preset);

export const getPresetsToShowInAddPanel = (
  component: AppDataComponent,
  addPanelDefinitionForBlocks?: PlatformAppCategoryDefinition,
): Preset[] | PlatformAppItemDefinition[] =>
  (
    (component.data as UnifiedWidget)?.presets ??
    addPanelDefinitionForBlocks?.items?.filter(
      (item) => item.widgetId === component.componentId,
    ) ??
    []
  ).filter(isValidPreset);

export const getItems = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  appData: AppData,
  component: AppDataComponent,
  addPanelDefinitionForBlocks?: PlatformAppCategoryDefinition,
): Item[] => {
  const presets = getPresetsToShowInAddPanel(
    component,
    addPanelDefinitionForBlocks,
  );

  return presets.map((preset) =>
    mapDefinitionToItem(
      documentServices,
      context,
      appData,
      component,
      preset,
      presets,
    ),
  );
};

export const isExtensionAllowed = (compType?: ComponentType) =>
  Boolean(compType && ALLOWED_EXTENSIONS.includes(compType));

export const shouldBeAdded = (component: AppDataComponent): boolean => {
  if (!isExtensionAllowed(component?.type as ComponentType)) {
    return false;
  }

  const compData = component?.data as AppUnifiedComponentData;
  return !compData?.installation?.base?.essential;
};

export const getAddPanelDefinitionForBlocks = (
  documentServices: DocumentServicesObject,
  appId: string,
): PlatformAppCategoryDefinition | undefined => {
  const appManifest: PlatformAppWithCategoryDefinition =
    documentServices.platform.getAppManifest(appId);
  return appManifest?.addPanelSectionDefinition;
};

export function getComponentsByOrder(
  appData: AppData,
  addPanelDefinitionForBlocks: PlatformAppCategoryDefinition | undefined,
): AppDataComponent[] {
  if (!addPanelDefinitionForBlocks) {
    return appData.components;
  }
  const addedBlocksComponentIds = new Set<string>();

  const orderedBlocksComponents = (
    addPanelDefinitionForBlocks.items ?? []
  ).reduce<AppDataComponent[]>((uniqueComponents, blocksWidget) => {
    if (
      blocksWidget.widgetId &&
      !addedBlocksComponentIds.has(blocksWidget.widgetId)
    ) {
      const component = appData.components.find(
        (component) => component.componentId === blocksWidget.widgetId,
      );

      if (component) {
        uniqueComponents.push(component);
        addedBlocksComponentIds.add(blocksWidget.widgetId);
      }
    }

    return uniqueComponents;
  }, []);

  const remainingComponents = appData.components.filter(
    (component) => !addedBlocksComponentIds.has(component?.componentId ?? ''),
  );

  return [...orderedBlocksComponents, ...remainingComponents];
}

export const getSections = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  app: EditorClientSpecMapEntry,
): Section[] => {
  if (app.isWixTPA) {
    return [];
  }

  const appData = documentServices.platform.getAppDataByAppDefId(
    app.appDefinitionId,
  ) as Required<AppData>;

  if (!appData?.components?.length) {
    return [];
  }
  const addPanelDefinitionForBlocks = getAddPanelDefinitionForBlocks(
    documentServices,
    app.appDefinitionId,
  );

  const components = getComponentsByOrder(appData, addPanelDefinitionForBlocks);

  return (
    components
      ?.filter(shouldBeAdded)
      .map((component: AppDataComponent) => ({
        component,
        items: getItems(
          documentServices,
          context,
          appData,
          component,
          addPanelDefinitionForBlocks,
        ),
      }))
      .filter(
        (componentWithItems: AppDataComponentWithItems) =>
          componentWithItems.items.length,
      )
      .map((componentWithItems: AppDataComponentWithItems, index) =>
        mapDefinitionToSection(componentWithItems, index),
      ) ?? []
  );
};
